/* AmourCoco — global helpers (deferred, every page). Section behaviour lives in each section's own script.
   window.theme: routes/strings/settings are set inline in layout/theme.liquid before this runs. */
(() => {
  'use strict';
  const theme = (window.theme = window.theme || {});
  theme.strings = theme.strings || {};
  theme.routes = theme.routes || {};
  theme.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- utilities ---------- */
  theme.debounce = (fn, ms = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(null, args), ms); };
  };

  theme.fetchJSON = async (url, opts = {}) => {
    const res = await fetch(url, {
      ...opts,
      headers: { Accept: 'application/json', ...(opts.body && !(opts.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}), ...(opts.headers || {}) }
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) throw Object.assign(new Error(data.description || data.message || res.statusText), { data, status: res.status });
    return data;
  };

  /* Money: supports Shopify money formats ({{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}} …). */
  theme.formatMoney = (cents, format = theme.moneyFormat || '£{{amount}}') => {
    const n = typeof cents === 'string' ? parseInt(cents.replace('.', ''), 10) : cents;
    const fmt = (num, dec, th = ',', d = '.') => {
      const [i, f] = (num / 100).toFixed(dec).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (f ? d + f : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => ({
      amount: fmt(n, 2),
      amount_no_decimals: fmt(n, 0),
      amount_with_comma_separator: fmt(n, 2, '.', ','),
      amount_no_decimals_with_comma_separator: fmt(n, 0, '.', ','),
      amount_with_apostrophe_separator: fmt(n, 2, "'", '.')
    }[key] ?? fmt(n, 2)));
  };

  const loaded = new Map();
  theme.loadScript = src => {
    if (!loaded.has(src)) {
      loaded.set(src, new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = src; s.defer = true; s.onload = resolve; s.onerror = reject;
        document.head.append(s);
      }));
    }
    return loaded.get(src);
  };
  theme.loadStyle = href => {
    if (document.querySelector(`link[href="${href}"]`)) return Promise.resolve();
    return new Promise(resolve => {
      const l = document.createElement('link');
      l.rel = 'stylesheet'; l.href = href; l.onload = resolve; l.onerror = resolve;
      document.head.append(l);
    });
  };

  /* ---------- events (pub/sub on document) ---------- */
  theme.publish = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));
  theme.subscribe = (name, fn) => {
    const h = e => fn(e.detail);
    document.addEventListener(name, h);
    return () => document.removeEventListener(name, h);
  };

  /* ---------- scroll lock (counted, so nested overlays work) ---------- */
  let locks = 0;
  theme.lockScroll = () => { locks += 1; document.body.classList.add('is-scroll-locked'); };
  theme.unlockScroll = () => { locks = Math.max(0, locks - 1); if (!locks) document.body.classList.remove('is-scroll-locked'); };

  /* ---------- page overlay (drawers) ---------- */
  const overlay = () => document.querySelector('[data-page-overlay]');
  theme.showOverlay = onClick => {
    const o = overlay(); if (!o) return;
    o.classList.add('is-active');
    o.onclick = onClick || null;
  };
  theme.hideOverlay = () => { const o = overlay(); if (o) { o.classList.remove('is-active'); o.onclick = null; } };

  /* ---------- focus trap ---------- */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  let trapKeydown = null;
  theme.focusable = el => [...el.querySelectorAll(FOCUSABLE)].filter(x => x.offsetParent !== null || x === document.activeElement);
  theme.trapFocus = (container, elementToFocus = container) => {
    theme.removeTrapFocus();
    trapKeydown = e => {
      if (e.key !== 'Tab') return;
      const els = theme.focusable(container);
      if (!els.length) { e.preventDefault(); return; }
      const first = els[0], last = els[els.length - 1];
      if (e.shiftKey && (document.activeElement === first || !container.contains(document.activeElement))) { last.focus(); e.preventDefault(); }
      else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
    };
    document.addEventListener('keydown', trapKeydown);
    requestAnimationFrame(() => elementToFocus?.focus({ preventScroll: true }));
  };
  theme.removeTrapFocus = elementToReturnTo => {
    if (trapKeydown) document.removeEventListener('keydown', trapKeydown);
    trapKeydown = null;
    elementToReturnTo?.focus?.({ preventScroll: true });
  };

  /* ---------- Section Rendering API helper ---------- */
  theme.sectionHTML = (html, selector) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return selector ? doc.querySelector(selector) : doc.body;
  };

  /* "2 packs + 1 free added" etc.: shown in the cart's notice and read out by its status region */
  theme.showCartNotice = text => {
    document.querySelectorAll('[data-cart-notice]').forEach(el => { el.textContent = text; el.hidden = false; });
    document.querySelectorAll('[data-cart-status]').forEach(el => { el.textContent = text; });
  };

  /* ---------- bundle offer: free line (as on the WordPress basket) ----------
     Products tagged offer-buy-X-get-Y: the quantity the shopper sets is the paid packs; the free packs sit on their
     own line (same variant, property _offer_free) that the shopper can't change. After every cart change (and on
     load) the free line is set to floor(paid / X) × Y, so it only follows the paid count. The automatic Buy X get Y
     discount makes those packs free at checkout. State comes from snippets/cart-offers-data in the cart sections. */
  const offersIn = root => {
    const el = root?.querySelector?.('[data-cart-offers]');
    if (!el) return null;
    try { return { lines: JSON.parse(el.textContent), notice: el.dataset.notice || '' }; } catch { return null; }
  };
  const offersFromSections = sections => {
    for (const html of Object.values(sections || {})) {
      const found = html && html.includes('data-cart-offers') && offersIn(theme.sectionHTML(html));
      if (found) return found;
    }
    return null;
  };
  let syncing = false;
  async function syncOffers(offers) {
    if (syncing || !offers?.lines.length) return;
    const jobs = offers.lines.map(o => ({ ...o, target: Math.floor(o.paid / o.buy) * o.get })).filter(o => o.target !== o.freeQuantity);
    if (!jobs.length) return;
    syncing = true;
    const ids = new Set(['cart-drawer', 'cart-icon-bubble']);
    document.querySelectorAll('cart-items[data-section-id]').forEach(el => ids.add(el.dataset.sectionId));
    const extra = { sections: [...ids], sections_url: window.location.pathname };
    const post = (url, body) => theme.fetchJSON(`${url}.js`, { method: 'POST', body: JSON.stringify({ ...body, ...extra }) });
    let data, added = [];
    try {
      for (const o of jobs) {
        // all free lines at once (Shopify may have split them): the first gets the target, the rest are cleared
        if (o.freeKeys.length) data = await post(theme.routes.cart_update_url, { updates: Object.fromEntries(o.freeKeys.map((k, i) => [k, i ? 0 : o.target])) });
        else data = await post(theme.routes.cart_add_url, { items: [{ id: o.variant, quantity: o.target, properties: { _offer_free: '1' } }] });
        if (o.target > o.freeQuantity) added.push(o);
      }
    } catch {
      // out of stock or a network error: the paid packs stay as they are
    }
    syncing = false;
    if (!data) return;
    theme.publish('cart:update', { source: 'offer-sync', sections: data.sections });
    const text = added.map(o => offers.notice.replace('[title]', o.title).replace('[buy]', o.buy).replace('[get]', o.get)).join(' ');
    if (text) theme.showCartNotice(text);
  }
  theme.subscribe('cart:update', async ({ sections, source } = {}) => {
    if (source === 'offer-sync') return;
    let offers = offersFromSections(sections);
    if (!offers) {
      try { offers = offersFromSections(await theme.fetchJSON(`${theme.routes.cart_url}?sections=cart-drawer`)); } catch {}
    }
    syncOffers(offers);
  });
  // on load too (e.g. the cart-page setting, where adding to cart is a plain form post)
  document.addEventListener('DOMContentLoaded', () => syncOffers(offersIn(document)));

  /* ---------- <quantity-input>: − n + stepper ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.addEventListener('click', e => {
        const b = e.target.closest('button[name]'); if (!b) return;
        e.preventDefault();
        const prev = this.input.value;
        b.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
        if (prev !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      this.input.addEventListener('change', () => this.update());
      this.update();
    }
    update() {
      const v = +this.input.value, min = +(this.input.min || 0), max = this.input.max ? +this.input.max : Infinity;
      const minus = this.querySelector('button[name="minus"]'), plus = this.querySelector('button[name="plus"]');
      if (minus) minus.disabled = v <= min;
      if (plus) plus.disabled = v >= max;
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---------- quick view (lazy: quick-view.js + its CSS load on first use) ---------- */
  theme.openQuickView = async (url, trigger) => {
    try {
      trigger?.classList.add('is-loading');
      await Promise.all([theme.loadStyle(theme.assets.quickViewCss), theme.loadScript(theme.assets.quickView), theme.loadScript(theme.assets.productForm)]);
      await customElements.whenDefined('quick-view-modal');
      await document.getElementById('QuickView')?.open(url, trigger);
    } catch {
      window.location.href = url; // fall back to the product page
    } finally {
      trigger?.classList.remove('is-loading');
    }
  };
  document.addEventListener('click', e => {
    const t = e.target.closest('[data-quick-view]');
    if (!t || e.defaultPrevented) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; // let new-tab clicks through
    e.preventDefault();
    theme.openQuickView(t.dataset.quickView, t);
  });
  // whole product card is clickable (opens quick view) except its own links/buttons/forms
  document.addEventListener('click', e => {
    const card = e.target.closest('[data-quick-view-card]');
    if (!card || e.defaultPrevented || e.target.closest('a, button, form, input, label')) return;
    const link = card.querySelector('[data-quick-view]');
    if (link) { e.preventDefault(); theme.openQuickView(link.dataset.quickView, link); }
  });
  // warm the cache on intent (hover/focus) so the quick view opens instantly
  const warm = e => {
    const t = e.target.closest?.('[data-quick-view], [data-quick-view-card]'); if (!t) return;
    theme.loadScript(theme.assets.quickView).catch(() => {});
    theme.loadStyle(theme.assets.quickViewCss);
  };
  document.addEventListener('pointerover', warm, { passive: true, once: true });
  document.addEventListener('focusin', warm, { once: true });

  /* ---------- AJAX customer forms (newsletter, notify me): no page reload ---------- */
  document.addEventListener('submit', async e => {
    const form = e.target.closest('form.notify-form, form[data-ajax-customer]');
    if (!form) return;
    e.preventDefault();
    const email = form.querySelector('input[type="email"]');
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.value.trim())) {
      email.setAttribute('aria-invalid', 'true');
      showFormMessage(form, theme.strings.emailInvalid, true);
      email.focus();
      return;
    }
    try {
      const res = await fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'text/html' } });
      // Shopify redirects to a captcha page when it suspects a bot: fall back to a normal submit
      if (/challenge/.test(res.url)) { form.submit(); return; }
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = form.id && doc.getElementById(form.id);
      if (fresh) {
        form.replaceWith(fresh);
        (fresh.querySelector('[role="status"], [role="alert"]') || fresh).focus?.();
      } else form.submit();
    } catch {
      form.submit();
    }
  });
  function showFormMessage(form, text, isError) {
    let m = form.querySelector('[data-form-message]');
    if (!m) {
      m = document.createElement('p');
      m.dataset.formMessage = '';
      m.setAttribute('role', 'alert');
      form.append(m);
    }
    m.className = `form-message ${isError ? 'form-message--error' : 'form-message--success'}`;
    m.textContent = text;
  }

  /* ---------- customer form results ----------
     form.posted_successfully? / form.errors are page-wide per form type, so after a normal (non-AJAX) submit every
     newsletter / notify-me form would show the result. Shopify adds the submitted form's id as the URL hash:
     keep the result only on that form and restore the others. */
  (() => {
    const hash = decodeURIComponent(location.hash.slice(1));
    if (!hash) return;
    document.querySelectorAll('form').forEach(form => {
      if (form.id === hash) return;
      const success = form.querySelector('[data-form-success]'), errors = form.querySelector('[data-form-errors]');
      if (!success && !errors) return;
      success?.remove();
      errors?.remove();
      form.querySelector('[data-form-fields]')?.removeAttribute('hidden');
      form.querySelectorAll('[aria-invalid]').forEach(i => { i.removeAttribute('aria-invalid'); i.removeAttribute('aria-describedby'); });
    });
  })();

  /* ---------- "Manage cookies": Shopify's cookie banner preferences (falls back to the privacy policy link) ---------- */
  document.addEventListener('click', e => {
    const link = e.target.closest('[data-cookie-preferences]');
    if (!link || typeof window.privacyBanner?.showPreferences !== 'function') return;
    e.preventDefault();
    window.privacyBanner.showPreferences();
  });

  /* ---------- scroll reveal ---------- */
  function reveal(root = document) {
    const els = root.querySelectorAll('.reveal:not(.is-revealed)');
    if (!document.documentElement.classList.contains('reveal-enabled') || theme.reducedMotion || !('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('is-revealed'));
      return;
    }
    theme._io = theme._io || new IntersectionObserver(entries => entries.forEach(en => {
      if (en.isIntersecting) { en.target.classList.add('is-revealed'); theme._io.unobserve(en.target); }
    }), { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach(el => theme._io.observe(el));
  }
  theme.reveal = reveal;
  reveal();
  // theme editor: re-run when sections are added / re-rendered
  document.addEventListener('shopify:section:load', e => reveal(e.target));
})();
